import { inject } from 'inversify';

import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Attachment } from '@/entities/Attachment';
import { Logger } from '@/services/Logger';
import {
  RecurringTripAttachmentReqMsg,
  TransportRequestAttachmentReqMsg,
  TripAttachmentReqMsg,
} from '@/services/signalr/MessageTypes/Attachment';
import { Signalr } from '@/services/signalr/Signalr';
import { AttachmentStore } from '@/stores/AttachmentStore';
import { IAttachmentInfo } from '~/api/IAttachmentInfo';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';
import { ITripAttachmentRespMsg } from '~/api/messages/ITripAttachmentRespMsg';

@SignalrListener
@service(AttachmentManager)
export class AttachmentManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(AttachmentStore) protected attachmentStore: AttachmentStore;
  @inject(UserController) protected userController: UserController;

  // Get all attachments for a trip
  getAttachmentsByTrip(transportId: string): IAttachmentInfo[] {
    return this.attachmentStore.all().filter((attachment) => attachment.id === transportId);
  }

  getRecurringAttachmentsBySeriesId(recurringSeriesId: string): IAttachmentInfo[] {
    return Array.from(this.attachmentStore.recurringAttachmentList)
      .filter((attachment) => attachment.id === recurringSeriesId);
  }

  populateExistingAttachments(attachments: IAttachmentInfo[]): void {
    attachments.forEach((attachmentData) => {
      this.attachmentStore.update(new Attachment(attachmentData));
    });
  }

  populateExistingRecurringAttachments(attachments: IAttachmentInfo[]): void {
    attachments.forEach((attachmentData) => {
      this.attachmentStore.addRecurringAttachment(new Attachment(attachmentData));
    });
  }

  exists(attachmentId: number): boolean {
    return !!this.attachmentStore.find(attachmentId);
  }

  // Send attachment info via SignalR
  async addAttachment(
    attachment: IAttachmentInfo,
    attachToRelatedTrips: boolean = false,
  ): Promise<ITripAttachmentRespMsg> {
    const response = await this.signalr.invoke(TripAttachmentReqMsg, {
      Attachment: attachment,
      AttachToRelatedTrips: String(attachToRelatedTrips),
    });

    return response;
  }

  async addTransportRequestAttachment(
    attachment: IAttachmentInfo,
  ): Promise<ITripAttachmentRespMsg> {
    const response = await this.signalr.invoke(TransportRequestAttachmentReqMsg, {
      Attachment: attachment,
    });

    return response;
  }

  async addRecurringAttachment(
    attachment: IAttachmentInfo,
  ): Promise<ITripAttachmentRespMsg> {
    const response = await this.signalr.invoke(RecurringTripAttachmentReqMsg, {
      Attachment: attachment,
    });

    return response;
  }

  // Handle attachment response from SignalR
  @SignalrMessage('TripAttachmentRespMsg')
  async onTripAttachmentRespMsg(response: ITripAttachmentRespMsg & IDispatchMessage) {
    if (response.attachment) {
      if (response.attachment.status === 0) {
        if (response.attachment.attachmentID !== undefined) {
          this.attachmentStore.remove(response.attachment.attachmentID);
        }
      }
      else {
        this.attachmentStore.update(new Attachment(response.attachment));
      }
    }
  }

  @SignalrMessage('RecurringTripAttachmentRespMsg')
  async onRecurringTripAttachmentRespMsg(response: ITripAttachmentRespMsg & IDispatchMessage) {
    if (response.attachment) {
      if (response.attachment.status === 0) {
        if (response.attachment.attachmentID !== undefined) {
          this.attachmentStore.removeRecurringAttachment(response.attachment.attachmentID);
        }
      }
      else {
        this.attachmentStore.addRecurringAttachment(new Attachment(response.attachment));
      }
    }
  }

  @SignalrMessage('TransportRequestAttachmentRespMsg')
  async onTransportRequestAttachmentRespMsg(response: ITripAttachmentRespMsg & IDispatchMessage) {
    if (response.attachment) {
      if (response.attachment.status === 0) {
        if (response.attachment.attachmentID !== undefined) {
          this.attachmentStore.remove(response.attachment.attachmentID);
        }
      }
      else {
        this.attachmentStore.update(new Attachment(response.attachment));
      }
    }
  }
}
